import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import MunicipalityIndex from "@/components/local/MunicipalityIndex";
import {
  Filter,
  Flag,
  Info,
  Loader2,
  MapPin,
  Navigation,
  PlugZap,
  Search,
  Zap,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import BreadcrumbNav from "@/components/layout/BreadcrumbNav";
import Footer from "@/components/landing/Footer";
import ElavtalSearchCard from "@/components/common/ElavtalSearchCard";
import AppPromoBanner from "@/components/laddstationer/AppPromoBanner";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { seoHead } from "@/config/seoHead";

const ChargingMap = lazy(() => import("@/components/laddstationer/ChargingMap"));

const DESCRIPTION =
  "Hitta laddstationer i hela Sverige. Interaktiv karta över laddstationer och snabbladdare för elbil – se var du kan ladda nära dig.";

export const Route = createFileRoute("/laddstationer/")({
  head: () =>
    seoHead("/laddstationer", {
      title: "Laddstationer i Sverige » Karta över elbilsladdare | Elmarknad.se",
      description: DESCRIPTION,
    }),
  component: LaddstationerPage,
});

function LaddstationerPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const [stats, setStats] = useState<{ total: number; fast: number } | null>(null);
  const onStats = useCallback((st: { total: number; fast: number }) => setStats(st), []);

  return (
    <div className="min-h-dvh bg-background">
      <Navbar />
      <BreadcrumbNav
        items={[
          { label: "Hem", path: "/", current: false },
          { label: "Laddstationer", path: null, current: true },
        ]}
      />

      <div className="page-container py-6 text-center md:py-8">
        <h1 className="heading-1 mb-2">Laddstationer i Sverige</h1>
        <p className="mx-auto max-w-2xl text-sm text-muted-foreground md:text-base">
          Hitta laddstationer och snabbladdare för elbil i hela landet. Klicka på en
          station i kartan eller listan för att se operatör, uttag, effekt och
          vägbeskrivning.
        </p>
      </div>

      {/* Fullbredd karta direkt under rubriken */}
      <section className="w-full">
        {mounted ? (
          <Suspense
            fallback={
              <div className="flex h-[calc(100dvh-220px)] min-h-[480px] w-full items-center justify-center bg-muted text-muted-foreground">
                <Loader2 className="mr-2 animate-spin" size={20} /> Laddar karta…
              </div>
            }
          >
            <ChargingMap onStats={onStats} />
          </Suspense>
        ) : (
          <div className="flex h-[calc(100dvh-220px)] min-h-[480px] w-full animate-pulse items-center justify-center bg-muted text-muted-foreground">
            <Loader2 className="mr-2 animate-spin" size={20} /> Laddar karta…
          </div>
        )}
      </section>

      <AppPromoBanner />

      <main className="section-stripe">
        <div className="page-container space-y-16 pb-16 pt-12">

          {/* Funktioner i tjänsten */}
          <section>
            <h2 className="heading-2 mb-2 text-center">Så hittar du rätt laddstation</h2>
            <p className="mx-auto mb-10 max-w-2xl text-center text-muted-foreground">
              Kartan samlar laddstationer i hela Sverige – med möjlighet att även visa
              Norge, Finland och Danmark. Här är verktygen som hjälper dig att hitta rätt.
            </p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="p-6">
                <Search className="mb-3 text-primary" size={28} />
                <h3 className="heading-3 mb-2">Sök på ort eller postnummer</h3>
                <p className="text-muted-foreground">
                  Skriv in en ort, adress eller ett postnummer i sökfältet så zoomar kartan
                  in dit och visar stationerna i närheten, sorterade efter avstånd.
                </p>
              </Card>
              <Card className="p-6">
                <Navigation className="mb-3 text-primary" size={28} />
                <h3 className="heading-3 mb-2">Använd din plats</h3>
                <p className="text-muted-foreground">
                  Med ett klick hämtar kartan din position och visar de närmaste
                  laddstationerna runt dig – perfekt när du är på resande fot.
                </p>
              </Card>
              <Card className="p-6">
                <Filter className="mb-3 text-primary" size={28} />
                <h3 className="heading-3 mb-2">Filtrera på det du behöver</h3>
                <p className="text-muted-foreground">
                  Filtrera på operatör, uttagstyp och snabbladdning. Du kan också välja
                  vilka länder som ska visas – Sverige är förvalt.
                </p>
              </Card>
              <Card className="p-6">
                <PlugZap className="mb-3 text-primary" size={28} />
                <h3 className="heading-3 mb-2">Detaljer om varje station</h3>
                <p className="text-muted-foreground">
                  Klicka på en station för att se operatör, adress, antal laddplatser,
                  uttagstyper och effekt per uttag – samt en direkt länk till
                  vägbeskrivning.
                </p>
              </Card>
              <Card className="p-6">
                <Zap className="mb-3 text-primary" size={28} />
                <h3 className="heading-3 mb-2">Hitta snabbladdare</h3>
                <p className="text-muted-foreground">
                  Slå på filtret för snabbladdning för att bara se DC-laddare med hög
                  effekt – de som laddar bilen till cirka 80 procent på 20–40 minuter.
                </p>
              </Card>
              <Card className="p-6">
                <Flag className="mb-3 text-primary" size={28} />
                <h3 className="heading-3 mb-2">Rapportera fel</h3>
                <p className="text-muted-foreground">
                  Ser du en station som är ur funktion, felplacerad eller saknas? Rapportera
                  det direkt från stationens detaljvy så hjälper du andra elbilsförare.
                </p>
              </Card>
            </div>
          </section>

          {/* Elavtal CTA */}
          <section className="mx-auto max-w-xl">
            <h2 className="heading-2 mb-2 text-center">Ladda hemma billigare</h2>
            <p className="mb-6 text-center text-muted-foreground">
              Har du elbil påverkar ditt elavtal laddkostnaden mycket. Gör en kostnadsfri
              jämförelse utifrån din avtalstyp, förbrukning och ditt elområde.
            </p>
            <ElavtalSearchCard title="Jämför elavtal – kostnadsfritt" />
          </section>

          {/* Om datan + laddtyper */}
          <section className="grid gap-6 lg:grid-cols-2">
            <Card className="p-6 md:p-8">
              <Info className="mb-3 text-primary" size={28} />
              <h2 className="heading-3 mb-2">Om datan</h2>
              <p className="text-muted-foreground">
                Laddstationerna kommer från OpenChargeMap, ett öppet och community-drivet
                register över laddstationer världen över. Utbyggnaden går snabbt, så antalet
                stationer kan skilja sig åt mot verkligheten från dag till dag. Kartan visar
                stationerna i det område du tittar på – zooma in för att ladda fler.
                Kontrollera alltid tillgänglighet och pris hos respektive operatör innan du
                åker.
              </p>
            </Card>
            <Card className="p-6 md:p-8">
              <Zap className="mb-3 text-primary" size={28} />
              <h2 className="heading-3 mb-2">Snabbladdning vs vanlig laddning</h2>
              <p className="text-muted-foreground">
                Snabbladdare (DC-laddning, ofta 50 kW eller mer) kan ladda en elbil till
                cirka 80 procent på 20–40 minuter och passar längre resor. Vanliga
                laddstationer (AC-laddning) tar flera timmar men är ofta billigare och
                vanligare vid parkeringar, köpcentrum och bostadsområden.
              </p>
            </Card>
          </section>

          {/* Laddstationer per kommun */}
          <section>
            <MunicipalityIndex
              basePath="/laddstationer"
              heading="Laddstationer i din kommun"
              description="Välj kommun för en lokal karta med antal laddplatser, snabbladdare, operatörer och uttag."
            />
          </section>

          {/* FAQ */}
          <section className="mx-auto max-w-3xl">
            <h2 className="heading-2 mb-6 text-center">Vanliga frågor</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="kostnad">
                <AccordionTrigger>Vad kostar det att ladda?</AccordionTrigger>
                <AccordionContent>
                  Priset sätts av respektive operatör och varierar mellan stationer. Många
                  visar pris i sin app eller på plats. Hemmaladdning är oftast billigast –
                  då spelar ditt elavtal stor roll för kostnaden.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="uttag">
                <AccordionTrigger>Vilket uttag passar min bil?</AccordionTrigger>
                <AccordionContent>
                  De vanligaste uttagen är Typ 2 för vanlig laddning och CCS för
                  snabbladdning. Klicka på en station i kartan för att se exakt vilka
                  uttag och effekter som finns där, och filtrera på uttagstyp om du vet vad
                  din bil använder.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="tackning">
                <AccordionTrigger>Vilka områden täcker kartan?</AccordionTrigger>
                <AccordionContent>
                  Kartan visar hela Sverige som standard. I filtret kan du även slå på
                  Norge, Finland och Danmark om du till exempel planerar en resa utomlands.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="fel">
                <AccordionTrigger>En station stämmer inte – vad gör jag?</AccordionTrigger>
                <AccordionContent>
                  Öppna stationens detaljvy och använd funktionen för felrapportering. Din
                  rapport hjälper oss att hålla informationen aktuell för alla användare.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
